/**
 * Sjednocená gesture vrstva pro Graph2 — pan / zoom / tooltip přes JEDEN
 * Pointer-Events model (`@use-gesture/react`). Nahrazuje původní mouse/touch
 * split (`isMobile`), ruční touch math a passive-listener workaround.
 *
 * Viz docs/plans/GRAPH2-GESTURE-OVERHAUL.md.
 *
 * Klíčové vlastnosti:
 *   - `target: containerRef` + `eventOptions:{passive:false}` → nativní non-passive
 *     listenery → `preventDefault()` skutečně funguje (React 19 passive-root bug).
 *   - Pan = 1 prst / myš-drag; pinch = 2 prsty (kotveno na střed); wheel = zoom
 *     kotvený na kurzor; tap = tooltip; long-press = scrub crosshair; double-tap = reset.
 *   - Disambiguace pan vs tooltip: distance-first práh + long-press timer + `everMulti`
 *     (gesto s ≥2 prsty NIKDY neemituje tap → žádný phantom tap po pinchi).
 *   - `boundsRef` mirror → wheel/pinch math nezastarává, listener se váže jednou.
 *   - rAF-throttle commitu bounds → ≤1 React render / frame.
 *   - `editMode` gate (react-grid-layout drag) + desktop `chartLock` gate;
 *     mobil = gesto-passthrough (svislý tah scrolluje stránku přes `touch-action: pan-y`).
 *   - `isUserInteracting` přetrvává po gestu (auto-scroll v index.tsx se nevrátí na live,
 *     dokud uživatel explicitně neresetuje — double-tap / reset).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useGesture } from "@use-gesture/react";
import { useResizeObserver } from "@client/hooks/useResizeObserver";
import { useUIStore } from "@client/store/ui";

export const DEFAULT_BOUND = 1.5 * 24 * 60 * 60 * 1000;
export const ZOOM_FACTOR = 0.1;

/** Vzdálenost (px), nad kterou se z "pending" stane pan (a tap se zahodí). */
const MOVE_THRESHOLD_PX = 12;
/** Maximální trvání tapu (ms). 100 ms (původní) bylo příliš přísné — reálný tap je 100–250 ms. */
const TAP_MAX_MS = 500;
/** Držení bez pohybu → scrub crosshair. */
const LONG_PRESS_MS = 450;
/** Okno pro double-tap (reset zoomu). */
const DOUBLE_TAP_GAP_MS = 300;
const DOUBLE_TAP_DIST_PX = 32;
/** Bezpečnostní timeout pro mobilní tooltip (zmizí i bez dalšího tapu). */
const TOOLTIP_SAFETY_MS = 8000;

export type Bounds = { min: number; max: number };

type GestureMode = "idle" | "pending" | "pan" | "scrub";

export interface UseGraph2InteractionResult {
  containerRef: RefObject<HTMLDivElement | null>;
  bounds: Bounds;
  setBounds: (b: Bounds) => void;
  isPanning: boolean;
  isUserInteracting: boolean;
  containerWidth: number;
  containerHeight: number;
  /** Mobil tap / scrub tooltip ("plovoucí karta"); null = skryto. */
  showTooltipAt: { x: number; y: number; timestamp: number } | null;
  /** True když lze graf vůbec ovládat gesty (= !editMode). Řídí `touch-action` (mobil passthrough). */
  interactive: boolean;
  /** Desktop "lock" — myš panuje jen když true. Řídí cursor (touch ignoruje). */
  chartLock: boolean;
  /** Resetuje zoom na default 1.5 day okno kolem latest dat a obnoví live auto-scroll. */
  resetZoom: (latestTimestamp?: number) => void;
}

export const useGraph2Interaction = (): UseGraph2InteractionResult => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth, height: containerHeight } = useResizeObserver(containerRef);
  const chartLock = useUIStore((s) => s.chartLock);
  const editMode = useUIStore((s) => s.editMode);

  const [bounds, setBoundsState] = useState<Bounds>(() => ({ min: Date.now() - DEFAULT_BOUND, max: Date.now() }));
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [showTooltipAt, setShowTooltipAt] = useState<{ x: number; y: number; timestamp: number } | null>(null);

  // Mirror bounds do refu — gesture handlery čtou aktuální hodnotu bez stale closure
  // a bez re-bindu listenerů (kvůli #8: wheel se vázal při každé změně bounds).
  const boundsRef = useRef(bounds);
  useEffect(() => {
    boundsRef.current = bounds;
  }, [bounds]);

  // rAF-throttle: ≤1 setBounds / frame. Eager update boundsRef, aby sekvenční
  // wheel/pinch ticky správně skládaly před React commitem.
  const rafRef = useRef<number | null>(null);
  const pendingBoundsRef = useRef<Bounds | null>(null);
  const commitBoundsRaf = useCallback((b: Bounds) => {
    boundsRef.current = b;
    pendingBoundsRef.current = b;
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      if (pendingBoundsRef.current) setBoundsState(pendingBoundsRef.current);
    });
  }, []);

  // Přímý (ne-rAF) setter pro programové změny (auto-scroll, reset).
  const setBounds = useCallback((b: Bounds) => {
    boundsRef.current = b;
    setBoundsState(b);
  }, []);

  const resetZoom = useCallback(
    (latestTimestamp?: number) => {
      const now = Math.max(latestTimestamp ?? 0, Date.now());
      setBounds({ min: now - DEFAULT_BOUND, max: now });
      setIsUserInteracting(false);
      setShowTooltipAt(null);
    },
    [setBounds]
  );

  // --- gesture pracovní refy (žádný re-render per move) ---
  const modeRef = useRef<GestureMode>("idle");
  const everMultiRef = useRef(false);
  const panStartBoundsRef = useRef<Bounds>(bounds);
  const panAnchorMxRef = useRef(0);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipSafetyRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTapRef = useRef<{ t: number; x: number; y: number } | null>(null);

  const clearLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const clientXToTimestamp = useCallback((clientX: number): number | null => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return null;
    const x = clientX - rect.left;
    const b = boundsRef.current;
    return b.min + (x / rect.width) * (b.max - b.min);
  }, []);

  const showTooltipAtClient = useCallback(
    (clientX: number, clientY: number) => {
      const timestamp = clientXToTimestamp(clientX);
      if (timestamp == null) return;
      setShowTooltipAt({ x: clientX, y: clientY, timestamp });
      if (tooltipSafetyRef.current) clearTimeout(tooltipSafetyRef.current);
      tooltipSafetyRef.current = setTimeout(() => setShowTooltipAt(null), TOOLTIP_SAFETY_MS);
    },
    [clientXToTimestamp]
  );

  const hideTooltip = useCallback(() => {
    setShowTooltipAt(null);
    if (tooltipSafetyRef.current) {
      clearTimeout(tooltipSafetyRef.current);
      tooltipSafetyRef.current = null;
    }
  }, []);

  // Interactive = lze vůbec panovat/zoomovat. Mobil řešíme přes pointerType uvnitř
  // handlerů (gesto-passthrough), takže "interactive" pro touch je jen !editMode.
  // Pro desktop (mouse/pen-bez-touch) navíc vyžadujeme chartLock (zachovaný kontrakt).
  const interactive = !editMode;

  const pointerIsTouch = (event: Event): boolean =>
    "pointerType" in event &&
    ((event as PointerEvent).pointerType === "touch" || (event as PointerEvent).pointerType === "pen");

  useGesture(
    {
      onDrag: (state) => {
        const { movement, first, last, canceled, pinching, elapsedTime, xy, event, cancel } = state;
        if (editMode) {
          cancel();
          return;
        }
        const isTouch = pointerIsTouch(event);
        // Desktop (myš): vyžaduje chartLock. Mobil (touch): vždy povoleno (passthrough
        // řeší touch-action: pan-y — svislý tah scrolluje stránku, sem dojde jen horizontální).
        if (!isTouch && !chartLock) return;

        if (pinching) {
          everMultiRef.current = true;
          clearLongPress();
          cancel();
          return;
        }

        if (first) {
          modeRef.current = "pending";
          everMultiRef.current = false;
          panStartBoundsRef.current = boundsRef.current;
          panAnchorMxRef.current = 0;
          clearLongPress();
          // Long-press → scrub (jen pokud se mezitím nezačalo panovat).
          longPressTimerRef.current = setTimeout(() => {
            if (modeRef.current === "pending" && !everMultiRef.current) {
              modeRef.current = "scrub";
              setIsUserInteracting(true);
              showTooltipAtClient(xy[0], xy[1]);
              if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8);
            }
          }, LONG_PRESS_MS);
        }

        const [mx] = movement;
        const dist = Math.hypot(movement[0], movement[1]);

        if (modeRef.current === "pending" && dist > MOVE_THRESHOLD_PX) {
          modeRef.current = "pan";
          clearLongPress();
          hideTooltip();
          panStartBoundsRef.current = boundsRef.current;
          panAnchorMxRef.current = mx;
          setIsPanning(true);
          setIsUserInteracting(true);
        }

        if (modeRef.current === "pan") {
          const sb = panStartBoundsRef.current;
          const width = Math.max(1, containerWidth);
          const tpp = (sb.max - sb.min) / width;
          const deltaPx = mx - panAnchorMxRef.current;
          const dt = deltaPx * tpp;
          commitBoundsRaf({ min: sb.min - dt, max: sb.max - dt });
        } else if (modeRef.current === "scrub") {
          const timestamp = clientXToTimestamp(xy[0]);
          if (timestamp != null) setShowTooltipAt({ x: xy[0], y: xy[1], timestamp });
        }

        if (last) {
          clearLongPress();
          setIsPanning(false);
          const wasPending = modeRef.current === "pending";
          modeRef.current = "idle";
          if (wasPending && !everMultiRef.current && !canceled && dist <= MOVE_THRESHOLD_PX && elapsedTime < TAP_MAX_MS) {
            // TAP — double-tap → reset, jinak tooltip.
            const now = typeof performance !== "undefined" ? performance.now() : 0;
            const prev = lastTapRef.current;
            const isDouble =
              prev != null &&
              now - prev.t < DOUBLE_TAP_GAP_MS &&
              Math.hypot(xy[0] - prev.x, xy[1] - prev.y) < DOUBLE_TAP_DIST_PX;
            if (isDouble) {
              lastTapRef.current = null;
              resetZoom();
            } else {
              lastTapRef.current = { t: now, x: xy[0], y: xy[1] };
              showTooltipAtClient(xy[0], xy[1]);
            }
          }
        }
      },

      onPinch: (state) => {
        const { origin, da, first, memo, event, cancel } = state;
        if (editMode) {
          cancel();
          return;
        }
        const isTouch = pointerIsTouch(event);
        if (!isTouch && !chartLock) {
          cancel();
          return;
        }
        everMultiRef.current = true;
        clearLongPress();
        hideTooltip();

        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0) return memo;

        let m = memo as { startBounds: Bounds; startDist: number } | undefined;
        if (first || !m || !m.startDist) {
          m = { startBounds: boundsRef.current, startDist: da[0] || 1 };
        }
        const scale = (da[0] || m.startDist) / m.startDist; // >1 = zoom in
        if (!Number.isFinite(scale) || scale <= 0) return m;

        const pct = (origin[0] - rect.left) / rect.width;
        const startRange = m.startBounds.max - m.startBounds.min;
        const newRange = startRange / scale;
        const tAtCenter = m.startBounds.min + startRange * pct;
        commitBoundsRaf({ min: tAtCenter - newRange * pct, max: tAtCenter + newRange * (1 - pct) });
        setIsUserInteracting(true);
        return m;
      },

      onWheel: (state) => {
        const { event } = state;
        if (editMode || !chartLock) return; // wheel je desktop-only zoom, vyžaduje chartLock
        event.preventDefault();
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0) return;
        const pct = (event.clientX - rect.left) / rect.width;
        const b = boundsRef.current;
        const tAtCursor = b.min + (b.max - b.min) * pct;
        // deltaY < 0 = scroll up = zoom in
        const mult = event.deltaY < 0 ? 1 - ZOOM_FACTOR : 1 + ZOOM_FACTOR;
        commitBoundsRaf({
          min: tAtCursor - (tAtCursor - b.min) * mult,
          max: tAtCursor + (b.max - tAtCursor) * mult,
        });
        setIsUserInteracting(true);
      },
    },
    {
      target: containerRef,
      eventOptions: { passive: false },
      drag: { filterTaps: true },
      // pinch necháváme na pointer events (default), kotvíme přes `origin`
    }
  );

  // Right-click → reset zoom (desktop). Vázáno jednou; resetZoom je stabilní.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handler = (e: MouseEvent) => {
      e.preventDefault();
      resetZoom();
    };
    container.addEventListener("contextmenu", handler);
    return () => container.removeEventListener("contextmenu", handler);
  }, [resetZoom]);

  // Cleanup časovačů a rAF na unmount.
  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      if (tooltipSafetyRef.current) clearTimeout(tooltipSafetyRef.current);
    };
  }, []);

  return useMemo(
    () => ({
      containerRef,
      bounds,
      setBounds,
      isPanning,
      isUserInteracting,
      containerWidth,
      containerHeight,
      showTooltipAt,
      interactive,
      chartLock,
      resetZoom,
    }),
    [bounds, setBounds, isPanning, isUserInteracting, containerWidth, containerHeight, showTooltipAt, interactive, chartLock, resetZoom]
  );
};
